import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/shared/StatusPill";
import { Gauge, Radio, UserCheck, AlertTriangle, Layers, LayoutGrid, Users, Clock } from "lucide-react";

const TONE = { ok: "success", warning: "warning", critical: "danger" };
const catTone = { can_reassign_safely: "success", needs_another_human: "warning", needs_manager_decision: "danger" };
const line = { borderColor: "var(--line)" };

export default function Operations() {
  const [d, setD] = useState({ metrics: [], orders: [], resp: [], gaps: [], caps: null, tree: null, teams: [], asOf: null });
  const put = (k) => (r) => setD((s) => ({ ...s, [k]: r }));
  useEffect(() => {
    api.get("/platform/metrics").then((r) => put("metrics")(r.data.metrics)).catch(() => {});
    api.get("/platform/standing-orders").then((r) => put("orders")(r.data)).catch(() => {});
    api.get("/platform/standing-orders/runs").then((r) => put("runs")(r.data)).catch(() => {});
    api.get("/platform/coverage").then((r) => put("resp")(r.data.responsibilities)).catch(() => {});
    api.get("/platform/knowledge-gaps").then((r) => put("gaps")(r.data.gaps)).catch(() => {});
    api.get("/platform/capabilities").then((r) => put("caps")(r.data)).catch(() => {});
    api.get("/platform/entities/tree").then((r) => put("tree")(r.data)).catch(() => {});
    api.get("/platform/shared-teams").then((r) => put("teams")(r.data)).catch(() => {});
    const at = new Date(Date.now() - 30 * 864e5).toISOString();
    api.get("/platform/memory/as-of", { params: { at } }).then((r) => put("asOf")(r.data)).catch(() => {});
  }, []);
  const evaluate = async () => { const { data } = await api.post("/platform/standing-orders/evaluate"); const r = await api.get("/platform/standing-orders/runs"); put("runs")(r.data); put("orders")((await api.get("/platform/standing-orders")).data); return data; };

  return (
    <div className="max-w-7xl mx-auto space-y-8" data-testid="operations-page">
      <div className="rise">
        <p className="eyebrow mb-1">Administration</p>
        <h1 className="font-heading text-3xl font-semibold">Operations intelligence</h1>
        <p className="text-sm text-slate-500 mt-1">The governed layer Atlas reasons over: metrics, standing orders, coverage, scope, capabilities and time.</p>
      </div>

      <Section icon={Gauge} title="Governed metrics" testid="metrics-scorecard">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{d.metrics.map((m) => <MetricCard key={m.name} m={m} />)}</div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section icon={Radio} title="Standing orders" testid="standing-orders">
          <div className="flex items-center justify-between mb-2"><p className="text-[11px] text-slate-500">{d.runs?.last_cron ? `Background evaluator last ran ${new Date(d.runs.last_cron.started_at).toLocaleString()}` : "Background evaluator runs every 15 minutes"}</p><button onClick={evaluate} className="text-xs underline text-slate-600" data-testid="standing-orders-evaluate">Evaluate now</button></div>
          <div className="space-y-2">
            {d.orders.length === 0 && <p className="text-sm text-slate-400">No standing orders.</p>}
            {d.orders.map((o) => <OrderCard key={o.id} o={o} />)}
          </div>
          {d.runs?.runs?.length > 0 && <div className="panel p-3 mt-3" data-testid="standing-order-runs"><p className="eyebrow mb-1">Recent evaluations</p>{d.runs.runs.slice(0, 6).map((r) => <div key={r.id} className="text-xs text-slate-600 py-1 border-t first:border-0" style={line}><span className="font-medium text-slate-800">{r.order_name}</span> · {r.condition_met ? `${r.subjects.length} match(es)` : "clear"} · {r.approval_required ? `${r.proposed} decision(s) proposed` : `${r.performed} action(s) performed`} · {r.actor.name} · {new Date(r.created_at).toLocaleTimeString()}</div>)}</div>}
        </Section>
        <Section icon={Layers} title="Hierarchical scope" testid="entity-tree">
          <div className="panel p-4">
            {!d.tree?.tree?.length && <p className="text-sm text-slate-400">Single-entity tenant — everything is tenant-wide.</p>}
            {flatten(d.tree?.tree).map((n) => <TreeRow key={n.id} n={n} depth={n.depth} />)}
            {d.tree?.scope && <ScopeNote s={d.tree.scope_source} />}
          </div>
          {d.teams.length > 0 && <TeamsCard teams={d.teams} />}
        </Section>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section icon={UserCheck} title="Responsibilities & coverage" testid="responsibilities">
          <div className="space-y-2">{d.resp.map((r) => <RespCard key={r.id} r={r} />)}</div>
        </Section>
        <Section icon={AlertTriangle} title="Knowledge gaps" testid="knowledge-gaps">
          <div className="space-y-2">
            {d.gaps.length === 0 && <p className="text-sm text-slate-400">No open knowledge gaps.</p>}
            {d.gaps.map((g, i) => <GapCard key={i} g={g} />)}
          </div>
        </Section>
      </div>

      <Section icon={LayoutGrid} title="Application capability registry" testid="capability-registry">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
          {d.caps && [d.caps.core, ...d.caps.applications].map((a) => <CapCard key={a.key} a={a} />)}
        </div>
      </Section>

      {d.asOf && <Section icon={Clock} title="Time-aware memory · state 30 days ago" testid="as-of"><AsOf a={d.asOf} /></Section>}
    </div>
  );
}

function Section({ icon: I, title, children, testid }) {
  return <section data-testid={testid}><h3 className="eyebrow mb-2 flex items-center gap-1.5"><I className="w-3.5 h-3.5" /> {title}</h3>{children}</section>;
}

function MetricCard({ m }) {
  const val = m.unit === "USD" ? `$${(m.value || 0).toLocaleString()}` : m.value;
  return (
    <div className="panel p-4">
      <div className="flex items-center justify-between"><span className="text-xs text-slate-500">{m.name}</span><StatusPill value={m.status} tone={TONE[m.status]} /></div>
      <div className="metric mt-2">{val}</div>
      <div className="text-[11px] text-slate-400 mt-1">{m.description}</div>
    </div>
  );
}

function OrderCard({ o }) {
  return (
    <div className="panel p-4 flex items-start gap-3">
      <div className="flex-1">
        <div className="font-medium text-slate-900 text-sm">{o.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{o.instruction}</div>
        <div className="flex gap-1.5 mt-2 flex-wrap">
          <span className="chip border-slate-200 text-slate-500">{o.trigger}</span>
          <span className="chip border-slate-200 text-slate-500">→ {o.action}</span>
          {o.approval_required && <span className="chip border-orange-200 text-orange-700 bg-orange-50">needs approval</span>}
          <span className="chip border-slate-200 text-slate-500">{o.scope}</span>
        </div>
      </div>
      <StatusPill value={o.status} tone="success" />
    </div>
  );
}

function ScopeNote({ s }) {
  return <p className="text-[11px] text-slate-400 mt-3 pt-3 hairline">Your view is scoped: {s?.direct?.length || 0} direct · {s?.inherited?.length || 0} inherited · via {s?.shared_teams?.join(", ") || "no teams"}</p>;
}

function TeamsCard({ teams }) {
  return (
    <div className="panel p-4 mt-3" data-testid="shared-teams">
      <p className="eyebrow mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Shared-service teams</p>
      {teams.map((t) => (
        <div key={t.id} className="text-sm py-1.5 border-t first:border-0" style={line}>
          <span className="font-medium">{t.name}</span> <span className="text-slate-500">· {t.function}</span>
          <div className="text-xs text-slate-500">Serves {t.entity_names.join(", ")} · {t.members.length} member(s)</div>
        </div>
      ))}
    </div>
  );
}

function RespCard({ r }) {
  return (
    <div className="panel p-3">
      <div className="flex items-center justify-between"><span className="font-medium text-slate-800 text-sm">{r.name}</span><StatusPill value={r.coverage_category?.replace(/_/g, " ")} tone={catTone[r.coverage_category]} /></div>
      <div className="text-xs text-slate-500 mt-1">Owner {r.primary_owner_name} · Backup {r.backup_owner_name || "none"} · {r.criticality}</div>
    </div>
  );
}

function GapCard({ g }) {
  return (
    <div className="panel p-3">
      <div className="flex items-center justify-between"><span className="eyebrow">{g.type?.replace(/_/g, " ")}</span><StatusPill value={g.severity} tone={g.severity === "high" ? "danger" : "warning"} /></div>
      <div className="text-sm text-slate-700 mt-1">{g.description}</div>
      <div className="text-xs mt-0.5" style={{ color: "var(--signal)" }}>{g.suggested_resolution}</div>
    </div>
  );
}

function CapCard({ a }) {
  const chip = a.enabled ? "border-teal-200 text-teal-700 bg-teal-50" : "border-slate-200 text-slate-400";
  const label = a.enabled ? (a.available_to_you ? "available" : "no access") : "disabled";
  return (
    <div className={`panel p-4 ${!a.available_to_you ? "opacity-50" : ""}`} data-testid={`capability-${a.key}`}>
      <div className="flex items-center justify-between"><span className="font-heading font-semibold">{a.name}</span><span className={`chip ${chip}`}>{label}</span></div>
      <CapRow l="Owns" v={a.records} /><CapRow l="Shares" v={a.shares} /><CapRow l="AI actions" v={a.ai_actions} /><CapRow l="Signals" v={a.signals} />
    </div>
  );
}

function CapRow({ l, v }) {
  if (!v?.length) return null;
  return <div className="mt-2"><div className="text-[10px] uppercase tracking-wider text-slate-400">{l}</div><div className="text-xs text-slate-700">{v.join(" · ")}</div></div>;
}

function AsOf({ a }) {
  const cells = [["Open tasks", a.open_tasks], ["Opportunities", a.opportunities], ["Pipeline", `$${(a.pipeline_value || 0).toLocaleString()}`], ["Items tracked", a.items_tracked], ["Units on hand", a.units_on_hand], ["Knowledge", a.knowledge_items]];
  return (
    <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
      {cells.map(([l, v]) => <div key={l} className="panel p-3"><div className="font-heading text-xl font-semibold tabular">{v}</div><div className="text-[10px] text-slate-500 uppercase tracking-wider">{l}</div></div>)}
    </div>
  );
}

function flatten(nodes, depth = 0, out = []) {
  (nodes || []).forEach((n) => { out.push({ ...n, depth }); flatten(n.children, depth + 1, out); });
  return out;
}

function TreeRow({ n, depth }) {
  return (
    <div style={{ paddingLeft: depth * 16 }}>
      <div className="flex items-center gap-2 py-1.5 text-sm">
        <span className={depth ? "w-1.5 h-1.5 rounded-full bg-slate-300" : "signal-dot"} />
        <span className="font-medium flex-1">{n.name}</span>
        <span className="text-[11px] text-slate-500 tabular">{n.inherited.open_opportunities} opps · {n.inherited.overdue_tasks} overdue · {n.inherited.members} scoped</span>
      </div>
    </div>
  );
}
